/**
 * FileReviewForm.jsx: BM-H632 (26 Sep 2026).
 *
 * The universal file review form. Everything it renders comes from
 * GET /api/compliance/file-reviews/form, the same definition the server checks
 * a submission against, so the screen cannot drift from the rules. The server
 * returns every unmet rule on submit; they are listed at the top and next to
 * the question they belong to.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, ClipboardCheck, Loader2, Plus, Trash2 } from "lucide-react";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

function authHeaders() {
  const email =
    sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || "";
  return email ? { "X-Bonsai-User-Id": email } : {};
}

function currentUser() {
  return (
    sessionStorage.getItem("bonsai_user_id") || localStorage.getItem("bonsai_user_id") || ""
  ).toLowerCase();
}

const INPUT =
  "w-full px-2 py-1.5 rounded-md text-sm bg-slate-900/60 border border-slate-700/60 text-slate-200 placeholder-slate-500 disabled:opacity-70";

const SAMPLE_REASON = {
  high_risk_monthly: "high-risk matter (monthly)",
  cqs_purchase: "conveyancing purchase (quarterly)",
  cqs_sale_or_remortgage: "conveyancing sale or remortgage (quarterly)",
  aml_quarterly: "AML sample, weighted by risk (quarterly)",
};

const HEADER_ROWS = [
  ["matter_ref", "Matter"],
  ["matter_name", "Matter name"],
  ["client_name", "Client"],
  ["fee_earner_email", "Fee earner"],
  ["date_opened", "Date opened"],
  ["date_of_review", "Date of review"],
  ["practice_area", "Practice area"],
  ["matter_type", "Matter type"],
  ["aml_risk_band", "AML risk band"],
  ["matter_status", "Matter status"],
  ["client_account_involved", "Client money involved"],
  ["prior_review_last_12_months", "Reviewed in the last 12 months"],
  ["closure_due_date", "Corrective-action closure due"],
];

// BM-H3771: dates as "21 May 2026", whatever shape they were stored in
// (older drafts hold the intake's full timestamp).
const DATE_FIELDS = new Set(["date_opened", "date_of_review", "closure_due_date"]);

function showDate(v) {
  const d = new Date(String(v).length === 10 ? `${v}T00:00:00` : v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function show(v, k) {
  if (k && DATE_FIELDS.has(k) && v) return showDate(v);
  if (v === true) return "Yes";
  if (v === false) return "No";
  if (v === null || v === undefined || v === "") return "Not recorded";
  return String(v);
}

function problemsFor(problems, prefix) {
  return (problems || []).filter((p) => p.field === prefix || p.field.startsWith(`${prefix}.`));
}

function Problems({ items }) {
  if (!items || !items.length) return null;
  return (
    <ul className="mt-1 space-y-0.5">
      {items.map((p, i) => (
        <li key={i} className="text-xs text-red-300">{p.message}</li>
      ))}
    </ul>
  );
}

function ChoiceGroup({ q, value, onChange, disabled }) {
  const selected = q.multi ? value || [] : value;
  const toggle = (v) => {
    if (q.multi) {
      const set = new Set(selected);
      if (set.has(v)) set.delete(v);
      else set.add(v);
      onChange([...set]);
    } else {
      onChange(v);
    }
  };
  return (
    <div className="flex flex-wrap gap-1.5" role={q.multi ? "group" : "radiogroup"}>
      {q.options.map((o) => {
        const on = q.multi ? selected.includes(o.value) : selected === o.value;
        const tone =
          o.concern === "material" ? "border-red-700/60" : o.concern === "gap" ? "border-amber-700/60" : "border-slate-600";
        return (
          <button
            key={o.value}
            type="button"
            disabled={disabled}
            onClick={() => toggle(o.value)}
            aria-pressed={on}
            className={`px-2.5 py-1 rounded-md text-xs border ${tone} ${
              on ? "bg-slate-200 text-slate-900" : "bg-slate-900/60 text-slate-300 hover:bg-slate-800"
            } disabled:opacity-70`}
            data-testid={`fr-${q.id}-${o.value}`}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

// BM-H3771: what CaseMinder's records say for this question, and on what.
function Derived({ q, answer }) {
  const d = answer?.derived;
  if (!d) return null;
  const label = (v) =>
    (Array.isArray(v) ? v : [v]).map((x) => (q.options.find((o) => o.value === x) || {}).label || x).join(", ");
  const changed =
    !d.unavailable && JSON.stringify(answer?.value ?? null) !== JSON.stringify(d.value ?? null);
  return (
    <div className="mt-2 p-2 rounded-md border border-sky-800/50 bg-sky-950/20 text-xs text-sky-200" data-testid={`fr-${q.id}-derived`}>
      <div>
        {d.unavailable ? (
          <span className="text-amber-300">From CaseMinder: could not check. Answer this yourself.</span>
        ) : (
          <>
            From CaseMinder: <span className="font-medium">{label(d.value)}</span>
            {changed && <span className="ml-2 text-amber-300" data-testid={`fr-${q.id}-overridden`}>You changed this answer.</span>}
          </>
        )}
      </div>
      <ul className="mt-1 space-y-0.5 text-sky-100/80">
        {(d.evidence || []).map((e, i) => (
          <li key={i}>
            {e.link ? (
              /^https?:/.test(e.link) ? (
                <a href={e.link} target="_blank" rel="noreferrer" className="underline">{e.text}</a>
              ) : (
                <Link to={e.link} className="underline">{e.text}</Link>
              )
            ) : e.text}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Question({ q, answer, setAnswer, disabled, problems, group }) {
  const opt = q.options.find((o) => o.value === answer?.value);
  const needsNote = opt?.concern === "material";
  return (
    <div className="py-3 border-t border-slate-800" data-testid={`fr-question-${q.id}`}>
      <div className="text-sm text-slate-100">
        {q.number}. {q.title}
      </div>
      <div className="text-xs text-slate-400 mb-2">{q.prompt}</div>
      <ChoiceGroup
        q={q}
        value={answer?.value}
        disabled={disabled}
        onChange={(v) => setAnswer({ ...(answer || {}), value: v })}
      />
      <Derived q={q} answer={answer} />
      {q.prompts && group && q.prompts[group] && (
        <ul className="mt-2 ml-4 list-disc text-xs text-slate-400" data-testid={`fr-${q.id}-prompts`}>
          {q.prompts[group].map((p) => <li key={p}>{p}</li>)}
        </ul>
      )}
      {q.prompts && !group && (
        <div className="mt-2 text-xs text-amber-300">
          The matter type is not recorded, so no matter-specific prompts are shown. Set the practice area above.
        </div>
      )}
      <textarea
        rows={needsNote ? 2 : 1}
        disabled={disabled}
        value={answer?.note || ""}
        onChange={(e) => setAnswer({ ...(answer || {}), note: e.target.value })}
        placeholder={needsNote ? "Explain this answer (required)" : "Note (optional)"}
        className={`${INPUT} mt-2 text-xs ${needsNote && !answer?.note ? "border-red-700/70" : ""}`}
        data-testid={`fr-${q.id}-note`}
      />
      <Problems items={problems} />
    </div>
  );
}

function ActionEditor({ actions, setActions, form, disabled, problems, questionList }) {
  const update = (i, patch) => setActions(actions.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const themeOptions = (questionList.find((q) => q.id === "q31")?.options) || [];
  return (
    <div className="space-y-3" data-testid="fr-actions">
      {actions.map((a, i) => (
        <div key={a.key || i} className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40 space-y-2" data-testid={`fr-action-${i}`}>
          <div className="flex items-center justify-between">
            <div className="text-xs text-slate-400">Action {i + 1}</div>
            {!disabled && (
              <button type="button" onClick={() => setActions(actions.filter((_, j) => j !== i))}
                className="text-slate-500 hover:text-red-300" aria-label={`Remove action ${i + 1}`}>
                <Trash2 size={14} />
              </button>
            )}
          </div>
          <textarea rows={2} disabled={disabled} value={a.action || ""} placeholder="Action required"
            onChange={(e) => update(i, { action: e.target.value })} className={INPUT} data-testid={`fr-action-${i}-text`} />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="text-xs text-slate-400">Linked question
              <select disabled={disabled} value={a.question || ""} onChange={(e) => update(i, { question: e.target.value || null })} className={INPUT}>
                <option value="">None</option>
                {questionList.filter((q) => !q.multi).map((q) => <option key={q.id} value={q.id}>{q.number}. {q.title}</option>)}
              </select>
            </label>
            <label className="text-xs text-slate-400">Severity
              <select disabled={disabled} value={a.severity || ""} onChange={(e) => update(i, { severity: e.target.value })} className={INPUT} data-testid={`fr-action-${i}-severity`}>
                <option value="">Choose</option>
                {form.action_severities.map((s) => <option key={s.value} value={s.value} title={s.guide}>{s.label}</option>)}
              </select>
            </label>
            <label className="text-xs text-slate-400">Owner (email)
              <input disabled={disabled} value={a.owner_email || ""} onChange={(e) => update(i, { owner_email: e.target.value })} className={INPUT} data-testid={`fr-action-${i}-owner`} />
            </label>
            <label className="text-xs text-slate-400">Due date
              <input type="date" disabled={disabled} value={a.due_date || ""} onChange={(e) => update(i, { due_date: e.target.value })} className={INPUT} data-testid={`fr-action-${i}-due`} />
            </label>
            <label className="text-xs text-slate-400">Linked risk
              <input disabled={disabled} value={a.linked_risk || ""} onChange={(e) => update(i, { linked_risk: e.target.value })} className={INPUT} />
            </label>
            <label className="text-xs text-slate-400">Linked policy
              <input disabled={disabled} value={a.linked_policy || ""} onChange={(e) => update(i, { linked_policy: e.target.value })} className={INPUT} />
            </label>
            <label className="text-xs text-slate-400">Escalate to
              <select disabled={disabled} value={a.escalate_to || ""} onChange={(e) => update(i, { escalate_to: e.target.value || null })} className={INPUT} data-testid={`fr-action-${i}-escalate`}>
                {form.escalation_targets.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </label>
          </div>
          <div className="flex flex-wrap gap-1.5 items-center">
            <span className="text-xs text-slate-400 mr-1">FWRA themes:</span>
            {themeOptions.map((t) => {
              const on = (a.themes || []).includes(t.value);
              return (
                <button key={t.value} type="button" disabled={disabled} aria-pressed={on}
                  onClick={() => update(i, { themes: on ? a.themes.filter((x) => x !== t.value) : [...(a.themes || []), t.value] })}
                  className={`px-2 py-0.5 rounded text-[11px] border border-slate-600 ${on ? "bg-slate-200 text-slate-900" : "text-slate-300"}`}
                  data-testid={`fr-action-${i}-theme-${t.value}`}>
                  {t.label}
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-4 text-xs text-slate-300">
            <label><input type="checkbox" disabled={disabled} checked={!!a.rereview_required} onChange={(e) => update(i, { rereview_required: e.target.checked })} /> Re-review required</label>
            <label><input type="checkbox" disabled={disabled} checked={!!a.training_required} onChange={(e) => update(i, { training_required: e.target.checked })} /> Training required</label>
          </div>
          <Problems items={problemsFor(problems, `actions.${i}`)} />
        </div>
      ))}
      {!disabled && (
        <button type="button" onClick={() => setActions([...actions, { themes: [] }])}
          className="px-3 py-1.5 rounded-md text-xs bg-slate-800 text-slate-200 border border-slate-700/60 hover:bg-slate-700 inline-flex items-center gap-1"
          data-testid="fr-add-action">
          <Plus size={12} /> Add corrective action
        </button>
      )}
    </div>
  );
}

function CorrectiveActions({ review, onChanged }) {
  const me = currentUser();
  const [notes, setNotes] = useState({});
  const close = async (a) => {
    const note = (notes[a.id] || "").trim();
    if (!note) {
      toast.error("Say what was done before closing the action.");
      return;
    }
    try {
      await axios.post(`${API}/compliance/file-reviews/${review.id}/actions/${a.id}/close`, { note }, { headers: authHeaders() });
      toast.success("Action closed.");
      onChanged();
    } catch (err) {
      toast.error(errorMessage(err, "Could not close the action"));
    }
  };
  const list = review.corrective_actions || [];
  if (!list.length) return <div className="text-xs text-slate-400">No corrective actions.</div>;
  return (
    <ul className="space-y-2" data-testid="fr-corrective-actions">
      {list.map((a) => (
        <li key={a.id} className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40 text-xs text-slate-300">
          <div className="text-slate-100 text-sm">{a.finding_text}</div>
          <div>{a.severity} · owner {a.owner_email} · due {a.due_date} · {a.status === "open" ? "Open" : `Closed: ${a.closure_note || ""}`}</div>
          {a.status === "open" && (me === (a.owner_email || "").toLowerCase() || me === (review.reviewer_email || "").toLowerCase()) && (
            <div className="flex gap-2 mt-2">
              <input value={notes[a.id] || ""} onChange={(e) => setNotes({ ...notes, [a.id]: e.target.value })}
                placeholder="What was done" className={INPUT} data-testid={`fr-close-note-${a.id}`} />
              <button type="button" onClick={() => close(a)}
                className="px-3 py-1 rounded-md bg-emerald-700/30 text-emerald-200 border border-emerald-700/60"
                data-testid={`fr-close-${a.id}`}>Close</button>
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

// BM-H638: a sampled review with no reviewer is assigned by the COLP.
function AssignReviewer({ review, onAssigned }) {
  const [staff, setStaff] = useState(null);
  const [rv, setRv] = useState("");
  useEffect(() => {
    axios.get(`${API}/compliance/file-reviews/sampling/settings`, { headers: authHeaders() })
      .then((r) => setStaff(r.data.staff || []))
      .catch(() => setStaff(null));
  }, []);
  if (!staff) return null;
  const assign = async () => {
    try {
      await axios.post(`${API}/compliance/file-reviews/${review.id}/assign`, { reviewer_email: rv }, { headers: authHeaders() });
      toast.success("Reviewer assigned.");
      onAssigned();
    } catch (err) {
      toast.error(errorMessage(err, "Could not assign the reviewer"));
    }
  };
  const fe = (review.fee_earner_email || "").toLowerCase();
  return (
    <div className="flex flex-wrap gap-2 items-center text-xs" data-testid="fr-assign">
      <span className="text-amber-300">No reviewer yet.</span>
      <select className={INPUT + " w-auto"} value={rv} onChange={(e) => setRv(e.target.value)} data-testid="fr-assign-select">
        <option value="">Choose a reviewer</option>
        {staff.filter((x) => x.email !== fe).map((x) => <option key={x.email} value={x.email}>{x.name}</option>)}
      </select>
      <button type="button" disabled={!rv} onClick={assign}
        className="px-3 py-1 rounded-md bg-emerald-700/30 text-emerald-200 border border-emerald-700/60 disabled:opacity-50"
        data-testid="fr-assign-btn">Assign</button>
    </div>
  );
}

export default function FileReviewForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [review, setReview] = useState(null);
  const [answers, setAnswers] = useState({});
  const [actions, setActions] = useState([]);
  const [conclusion, setConclusion] = useState({});
  const [headerEdits, setHeaderEdits] = useState({});
  const [problems, setProblems] = useState([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [f, r] = await Promise.all([
        axios.get(`${API}/compliance/file-reviews/form`, { headers: authHeaders() }),
        axios.get(`${API}/compliance/file-reviews/${id}`, { headers: authHeaders() }),
      ]);
      let rv = r.data;
      // BM-H3771: a draft made before answers were pre-filled from the
      // records gets them the first time it is opened.
      if (rv.status === "draft" && !rv.historic && !rv.answers_prefilled_at) {
        try {
          rv = (await axios.post(`${API}/compliance/file-reviews/${id}/prefill`, {}, { headers: authHeaders() })).data;
        } catch (err) {
          toast.error(errorMessage(err, "Could not pre-fill the answers from CaseMinder"));
        }
      }
      setForm(f.data);
      setReview(rv);
      setAnswers(rv.answers || {});
      setActions(rv.actions || []);
      setConclusion(rv.conclusion || {});
      setHeaderEdits({});
      setError("");
    } catch (err) {
      setError(errorMessage(err, "Could not load this review"));
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const questionList = useMemo(
    () => (form?.sections || []).flatMap((s) => s.questions),
    [form]
  );

  const editable = review?.status === "draft" && currentUser() === (review?.reviewer_email || "").toLowerCase();
  const header = { ...(review?.header || {}), ...headerEdits };

  const save = useCallback(async () => {
    const body = { answers, actions, conclusion };
    if (Object.keys(headerEdits).length) body.header = headerEdits;
    const r = await axios.put(`${API}/compliance/file-reviews/${id}`, body, { headers: authHeaders() });
    setReview((prev) => ({ ...r.data, corrective_actions: prev?.corrective_actions || [] }));
    setHeaderEdits({});
    return r.data;
  }, [answers, actions, conclusion, headerEdits, id]);

  const onSave = async () => {
    setBusy(true);
    try {
      await save();
      toast.success("Draft saved.");
    } catch (err) {
      const p = err?.response?.data?.detail?.problems;
      if (p) setProblems(p);
      toast.error(errorMessage(err, "Could not save"));
    } finally {
      setBusy(false);
    }
  };

  const onSubmit = async () => {
    setBusy(true);
    setProblems([]);
    try {
      await save();
      const r = await axios.post(`${API}/compliance/file-reviews/${id}/submit`, {}, { headers: authHeaders() });
      setReview(r.data);
      const errs = r.data.escalation_errors || [];
      if (errs.length) toast.error(`Submitted, but ${errs.length} escalation(s) to the MLRO failed. Tell the MLRO directly.`);
      else toast.success("Review submitted.");
    } catch (err) {
      const p = err?.response?.data?.detail?.problems;
      if (p) {
        setProblems(p);
        toast.error(`${p.length} thing(s) to complete before this can be submitted.`);
        window.scrollTo?.(0, 0);
      } else {
        toast.error(errorMessage(err, "Could not submit"));
      }
    } finally {
      setBusy(false);
    }
  };

  const onRefresh = async () => {
    setBusy(true);
    try {
      await save();
      const r = await axios.post(`${API}/compliance/file-reviews/${id}/prefill`, {}, { headers: authHeaders() });
      setReview((prev) => ({ ...r.data, corrective_actions: prev?.corrective_actions || [] }));
      setAnswers(r.data.answers || {});
      toast.success("Answers refreshed from CaseMinder. Your own answers were kept.");
    } catch (err) {
      toast.error(errorMessage(err, "Could not refresh from CaseMinder"));
    } finally {
      setBusy(false);
    }
  };

  const onDiscard = async () => {
    if (!window.confirm("Discard this draft review?")) return;
    try {
      await axios.post(`${API}/compliance/file-reviews/${id}/discard`, {}, { headers: authHeaders() });
      navigate("/compliance/file-reviews");
    } catch (err) {
      toast.error(errorMessage(err, "Could not discard"));
    }
  };

  if (error) {
    return <div className="p-6 text-sm text-red-300" data-testid="fr-error">{error}</div>;
  }
  if (!form || !review) {
    return (
      <div className="p-6 text-slate-400 text-sm flex items-center gap-2" data-testid="fr-loading">
        <Loader2 size={14} className="animate-spin" /> Loading…
      </div>
    );
  }

  const sources = review.header_sources || {};
  const setHeader = (k, v) => setHeaderEdits({ ...headerEdits, [k]: v });

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-4xl" data-testid="file-review-form">
      <Link to="/compliance/file-reviews" className="text-xs text-slate-400 hover:text-slate-200 inline-flex items-center gap-1">
        <ArrowLeft size={12} /> File reviews
      </Link>
      <div className="flex items-center gap-2">
        <ClipboardCheck size={18} className="text-emerald-300" />
        <h1 className="text-base font-semibold text-slate-100">File review: {review.matter_ref}</h1>
        <span className="text-xs text-slate-400" data-testid="fr-status">
          {review.status === "draft" ? "Draft" : review.status === "closed" ? "Closed" : "Submitted, actions open"}
        </span>
      </div>

      {problems.length > 0 && (
        <div className="p-3 rounded-md bg-red-950/40 border border-red-800/50" data-testid="fr-problems">
          <div className="flex items-center gap-2 text-red-200 text-sm mb-1">
            <AlertTriangle size={14} /> To complete before submitting
          </div>
          <Problems items={problems} />
        </div>
      )}

      {review.sampling_run_id && (
        <div className="p-3 rounded-md bg-sky-950/30 border border-sky-800/50 text-xs text-sky-200 space-y-2" data-testid="fr-sampled">
          <div>
            Sampled in the {review.sampling_run_id} run: {(review.sample_reasons || []).map((r) => SAMPLE_REASON[r] || r).join("; ")}.
            {review.review_due && ` Due by ${review.review_due}.`}
            {review.reviewer_email && ` Reviewer: ${review.reviewer_email} (${review.reviewer_assigned_how || "assigned"}).`}
          </div>
          {review.status === "draft" && !review.reviewer_email && <AssignReviewer review={review} onAssigned={load} />}
        </div>
      )}

      {review.independence_exception && (
        <div className="p-3 rounded-md bg-amber-950/30 border border-amber-800/50 text-xs text-amber-200">
          Independence exception authorised by {review.independence_exception.authorised_by}:{" "}
          {review.independence_exception.reason}
        </div>
      )}

      <section className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40" data-testid="fr-header">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
          {HEADER_ROWS.map(([k, label]) => (
            <div key={k}>
              <div className="text-[11px] text-slate-500">
                {label}
                <span className="ml-1 text-slate-600">({sources[k] || "reviewer"})</span>
              </div>
              {editable && k === "fee_earner_email" ? (
                <input className={INPUT} value={header[k] || ""} onChange={(e) => setHeader(k, e.target.value)} data-testid="fr-h-fee_earner_email" />
              ) : editable && k === "matter_status" ? (
                <select className={INPUT} value={header[k] || ""} onChange={(e) => setHeader(k, e.target.value)}>
                  {form.matter_statuses.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : editable && k === "client_account_involved" ? (
                <select className={INPUT} value={header[k] === true ? "yes" : header[k] === false ? "no" : ""}
                  onChange={(e) => setHeader(k, e.target.value === "" ? null : e.target.value === "yes")}
                  data-testid="fr-h-client_account_involved">
                  <option value="">Choose</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              ) : editable && (k === "practice_area" || k === "matter_type") ? (
                <input className={INPUT} value={header[k] || ""} onChange={(e) => setHeader(k, e.target.value)} />
              ) : editable && k === "closure_due_date" ? (
                <input type="date" className={INPUT} value={header[k] || ""} onChange={(e) => setHeader(k, e.target.value)} />
              ) : (
                <div className={`text-slate-200 ${show(header[k]) === "Not recorded" ? "text-amber-300" : ""}`} data-testid={`fr-h-${k}`}>{show(header[k], k)}</div>
              )}
              <Problems items={problemsFor(problems, `header.${k}`)} />
            </div>
          ))}
          <div>
            <div className="text-[11px] text-slate-500">Review</div>
            <div className="text-slate-200">
              {(form.review_types.find((t) => t.value === header.review_type) || {}).label} ·{" "}
              {(form.review_modes.find((t) => t.value === header.review_mode) || {}).label} · reviewer {review.reviewer_email}
            </div>
          </div>
          <div className="flex gap-3 items-end text-xs">
            <Link to={header.caseminder_url || `/matter/${review.matter_ref}`} className="text-sky-300 underline">Matter in CaseMinder</Link>
            {header.sharepoint_url && (
              <a href={header.sharepoint_url} target="_blank" rel="noreferrer" className="text-sky-300 underline">SharePoint folder</a>
            )}
          </div>
        </div>
      </section>

      {/* BM-H635: a review from before the universal form, as it was recorded. */}
      {review.historic && (
        <section className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40 space-y-2 text-sm" data-testid="fr-historic">
          <h2 className="text-sm font-semibold text-slate-100">Historic review, as recorded</h2>
          <div className="text-xs text-slate-400">
            Carried out before the universal file review form, so the form's questions were not answered.
            Loaded from {review.historic.source}.
          </div>
          {[
            ["Reviewer", `${review.historic.reviewer_name} (${review.historic.reviewer_role})`],
            ["Internal or external", review.historic.internal_external],
            ["Date", `${review.historic.date_as_recorded} (${review.historic.date_source})`],
            ["Matter type as recorded", review.historic.matter_type_as_recorded],
            ["Focus", review.historic.focus],
            ["Outcome", review.historic.outcome],
          ].map(([label, value]) => (
            <div key={label}>
              <div className="text-[11px] text-slate-500">{label}</div>
              <div className="text-slate-200" data-testid={`fr-historic-${label.split(" ")[0].toLowerCase()}`}>{value}</div>
            </div>
          ))}
        </section>
      )}

      {!review.historic && review.answers_prefilled_at && (
        <div className="p-3 rounded-md border border-sky-800/50 bg-sky-950/20 text-xs text-sky-200" data-testid="fr-prefill-note">
          Questions marked From CaseMinder were answered from the matter's records, with the evidence shown.
          Check each one and change it if the file says otherwise. The judgement questions are left for you.
        </div>
      )}

      {!review.historic && form.sections.map((s) => (
        <section key={s.id} className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40" data-testid={`fr-section-${s.id}`}>
          <h2 className="text-sm font-semibold text-slate-100">Section {s.number}: {s.title}</h2>
          {s.intro && <div className="text-xs text-slate-400">{s.intro}</div>}
          {s.rule && <div className="text-xs text-amber-300 mt-1">{s.rule}</div>}
          {s.questions.map((q) => (
            <Question
              key={q.id}
              q={q}
              group={review.matter_type_group}
              answer={answers[q.id]}
              disabled={!editable}
              problems={problemsFor(problems, `answers.${q.id}`)}
              setAnswer={(a) => setAnswers({ ...answers, [q.id]: a })}
            />
          ))}
          {s.id === "s8" && (
            <div className="pt-3 border-t border-slate-800">
              <div className="text-sm text-slate-100 mb-2">34. Action record</div>
              {review.status === "draft" ? (
                <ActionEditor actions={actions} setActions={setActions} form={form} disabled={!editable}
                  problems={problems} questionList={questionList} />
              ) : (
                <CorrectiveActions review={review} onChanged={load} />
              )}
              <Problems items={problemsFor(problems, "actions").filter((p) => p.field === "actions")} />
            </div>
          )}
        </section>
      ))}

      {!review.historic && (
      <section className="p-3 rounded-md border border-slate-700/60 bg-slate-900/40 space-y-2" data-testid="fr-conclusion">
        <h2 className="text-sm font-semibold text-slate-100">Section 9: Reviewer conclusion</h2>
        <div className="text-xs text-slate-400">37. Summary</div>
        {[["good", "What is good"], ["wrong", "What is wrong"], ["why", "Why it matters"], ["next", "What must happen next"]].map(([k, label]) => (
          <label key={k} className="block text-xs text-slate-400">{label}
            <textarea rows={2} disabled={!editable} className={INPUT}
              value={(conclusion.summary || {})[k] || ""}
              onChange={(e) => setConclusion({ ...conclusion, summary: { ...(conclusion.summary || {}), [k]: e.target.value } })}
              data-testid={`fr-summary-${k}`} />
            <Problems items={problemsFor(problems, `conclusion.summary.${k}`)} />
          </label>
        ))}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <label className="text-xs text-slate-400">38. Final outcome
            <select disabled={!editable} className={INPUT} value={conclusion.final_outcome || ""}
              onChange={(e) => setConclusion({ ...conclusion, final_outcome: e.target.value })} data-testid="fr-outcome">
              <option value="">Choose</option>
              {form.final_outcomes.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <Problems items={problemsFor(problems, "conclusion.final_outcome")} />
          </label>
          <label className="text-xs text-slate-400">39. Re-review date
            <input type="date" disabled={!editable} className={INPUT} value={conclusion.rereview_date || ""}
              onChange={(e) => setConclusion({ ...conclusion, rereview_date: e.target.value })} />
            <Problems items={problemsFor(problems, "conclusion.rereview_date")} />
          </label>
        </div>
        <div className="text-xs text-slate-400">40. Date closed: {review.closed_at ? new Date(review.closed_at).toLocaleDateString("en-GB") : "Open"}</div>
      </section>
      )}

      {editable && (
        <div className="flex flex-wrap gap-2 sticky bottom-0 py-3 bg-slate-950/90">
          <button type="button" disabled={busy} onClick={onSave}
            className="px-3 py-1.5 rounded-md text-sm bg-slate-800 text-slate-200 border border-slate-700/60 hover:bg-slate-700 disabled:opacity-50"
            data-testid="fr-save">Save draft</button>
          <button type="button" disabled={busy} onClick={onSubmit}
            className="px-3 py-1.5 rounded-md text-sm font-medium bg-emerald-700/30 text-emerald-200 border border-emerald-700/60 hover:bg-emerald-700/50 disabled:opacity-50"
            data-testid="fr-submit">Submit review</button>
          <button type="button" disabled={busy} onClick={onRefresh}
            className="px-3 py-1.5 rounded-md text-sm bg-slate-800 text-sky-200 border border-slate-700/60 hover:bg-slate-700 disabled:opacity-50"
            data-testid="fr-refresh">Refresh from CaseMinder</button>
          <button type="button" disabled={busy} onClick={onDiscard}
            className="px-3 py-1.5 rounded-md text-sm text-slate-400 hover:text-red-300" data-testid="fr-discard">Discard draft</button>
          {busy && <Loader2 size={14} className="animate-spin text-slate-400 self-center" />}
        </div>
      )}
    </div>
  );
}
